<script setup>
import { computed, onMounted } from 'vue';
import XlAvatar from '../xl/XlAvatar.vue';
import XlTag from '../xl/XlTag.vue';
import useHubBrokers from '../../composables/useHubBrokers';
import useHubSegments from '../../composables/useHubSegments';
import { accessTooltip, describeAccess, ownBookOf } from '../../composables/useResourceAccess';
import { CAPABILITIES, can, isBroker } from '../../helpers/roles';
import { useAuthStore } from '../../stores/auth';

const props = defineProps({
  access: { type: Object, default: null },
  // Resolved hub members [{ id, label, img }] so chips can show avatars/initials.
  members: { type: Array, default: () => [] },
  // Whether this particular row is the caller's to manage. A broker manages
  // only what they made — a capability alone cannot answer that, only the
  // caller knows which row this is. null keeps today's capability fallback,
  // for the callers (Agenda.vue, chat/EventResources.vue) that show a row
  // nobody but an owner/staff member ever manages.
  manageable: { type: Boolean, default: null },
  // What this row's "everyone" is to the viewer: true for their own book, a
  // broker's name for that broker's book, false for the whole hub.
  book: { type: [Boolean, String], default: false },
});

const emit = defineEmits(['manage']);

const authStore = useAuthStore();
// Who a row reaches is worth reading whoever you are; changing it is not for
// everyone. Without the capability this stays a tag rather than becoming a
// button that refuses the click — the caller's `manage` handler returns early
// anyway, so as a button it did nothing at all. Decided here rather than by a
// prop so every caller is covered, including any added later — except when
// the caller passes `manageable` explicitly, because a broker manages only
// their own rows and no capability check alone can tell the two apart.
const manageable = computed(() => (
  props.manageable === null
    ? can(authStore.userRole, CAPABILITIES.MANAGE_HUB_CONTENT, authStore.capabilities)
    : props.manageable
));

// A missing summary means two different things. To someone who manages content
// it is the default, and the default is Everyone. To everyone else it is
// withheld — `ResourceController::index` nulls it outright — so rendering it
// would announce "Everyone" over a file only a few people can open. Say
// nothing rather than say the wrong thing.
const tellable = computed(() => manageable.value || Boolean(props.access));

const { segments, loadHubSegments } = useHubSegments();
const { loadHubBrokers } = useHubBrokers();

onMounted(() => {
  if ((props.access?.segment_ids || []).length) loadHubSegments();
  if ((props.access?.broker_ids || []).length) loadHubBrokers();
});

const descriptor = computed(() => describeAccess(props.access, segments.value, { book: props.book, members: props.members, me: ownBookOf(authStore.userData) }));
// A static chip, or a broker's chip on a row that is not their book (it opens
// a read-only sheet), does not promise a change.
const changeable = computed(() => manageable.value && !(isBroker(authStore.userData) && props.book !== true));
const tooltip = computed(() => accessTooltip(props.access, props.members, segments.value, { book: props.book, changeable: changeable.value, me: ownBookOf(authStore.userData) }));

const people = computed(() =>
  (props.access?.member_ids || []).map((id) => props.members.find((m) => m.id === id) || { id, label: '' })
);

const named = computed(() => people.value.length > 0 && people.value.every((person) => person.label));

const icon = computed(() => (descriptor.value.kind === 'all' ? 'people' : 'lock'));

const handleClick = (event) => {
  if (!manageable.value) return;
  // The row may be a <label>; without this the click toggles the row checkbox.
  event.preventDefault();
  event.stopPropagation();
  emit('manage');
};
</script>

<template>
  <component
    v-if="tellable"
    :is="manageable ? 'button' : 'span'"
    :type="manageable ? 'button' : undefined"
    class="access-tag"
    :class="{ 'is-static': !manageable }"
    :title="tooltip"
    @click="handleClick"
  >
    <span v-if="descriptor.kind === 'members' && named" class="access-tag__chips">
      <XlAvatar
        v-for="person in people.slice(0, 2)"
        :key="person.id"
        class="access-tag__initials"
        :name="person.label"
        :src="person.img || ''"
        :color="person.color || ''"
        :size="28"
      />
      <span v-if="people.length === 1" class="access-tag__name">{{ people[0].label }}</span>
      <span v-else-if="people.length > 2" class="access-tag__more">+{{ people.length - 2 }}</span>
    </span>

    <XlTag v-else :tone="descriptor.tone" class="access-tag__pill">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <template v-if="icon === 'people'">
          <path d="M16 19v-1.6a3.4 3.4 0 0 0-3.4-3.4H6.4A3.4 3.4 0 0 0 3 17.4V19" /><circle cx="9.5" cy="7.5" r="3.1" /><path d="M21 19v-1.6a3.4 3.4 0 0 0-2.6-3.3M15.4 4.6a3.4 3.4 0 0 1 0 6.1" />
        </template>
        <template v-else>
          <rect x="4.5" y="10.4" width="15" height="9.6" rx="2.5" /><path d="M8 10.4V7.6a4 4 0 0 1 8 0v2.8" />
        </template>
      </svg>
      <span class="access-tag__label">{{ descriptor.label }}</span>
    </XlTag>
  </component>
</template>

<style lang="scss" scoped>
.access-tag {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;

  &.is-static {
    cursor: default;
  }
  display: inline-flex;
  align-items: center;
  max-width: min(100%, 260px);
  min-width: 0;
  font-family: var(--xl-font-family);

  &__pill {
    // The tag never wraps; the whole wording stays in the button's title.
    max-width: 100%;
    overflow: hidden;

    svg {
      flex: none;
    }
  }

  &__label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__chips {
    display: inline-flex;
    align-items: center;
  }

  &__initials {
    margin-left: -6px;
    border: 2px solid #fff;

    &:first-child {
      margin-left: 0;
    }
  }

  &__name {
    margin-left: 8px;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: var(--xl-type-body-small-size);
    color: var(--xl-color-secondary-text);
  }

  &__more {
    margin-left: 4px;
    font-size: var(--xl-type-body-small-size);
    color: var(--xl-color-secondary-text);
  }
}
</style>
